import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * A régua tipográfica do GenUI 2, declarada pro `tailwind-merge` como **tamanho de fonte**.
 *
 * Sem isto o `twMerge` classifica `text-caption` como COR — a heurística dele é que `text-<palavra>`
 * é cor, e ele não conhece as nossas. O efeito era um bug silencioso e difícil de achar: passar
 * `className="text-caption text-muted-foreground"` pra qualquer primitivo que rode `cn()` (`Label`,
 * `Button`, `Badge`…) fazia os dois brigarem pelo MESMO grupo, o último ganhava, e **o tamanho era
 * descartado sem erro nenhum**. O rótulo da faixa de campos do `FlightPlannerV2` saiu a 14px em vez
 * de 12 por causa disto, e o designer viu antes de mim: *"esse label não está muito grande, não?"*.
 *
 * Declarados aqui, eles passam a conflitar com o que de fato conflita — `text-sm`, `text-base` e os
 * degraus do Tailwind — e param de conflitar com cor. Token novo na régua (`../styles.css`, bloco
 * `@theme inline`) entra numa destas listas também.
 *
 * Os degraus que trazem peso (`--text-*--font-weight`) ficam num grupo à parte, que também
 * conflita com `font-*`. O utilitário do Tailwind escreve `font-weight: var(--tw-font-weight,
 * <peso do token>)`, então um `font-medium` de base vencia o token passado pelo consumidor:
 * `DialogTitle` com `text-heading` saía a 500, não 600. Com o conflito declarado, o token do
 * consumidor tira o peso da base; um `font-*` escrito DEPOIS do token continua ganhando.
 *
 * Com modificador de entrelinha (`text-heading/none`) o Tailwind escreve só tamanho e entrelinha,
 * sem o peso do token. Essa forma vai para `text-role-leading`, que não conflita com `font-*`: o
 * `font-medium` da base continua valendo, senão o texto cairia no peso herdado.
 */
const UNWEIGHTED = ['body', 'detail', 'caption', 'micro', 'nano'];
const WEIGHTED = [
  'greeting',
  'display',
  'headline',
  'page',
  'heading',
  'subtitle',
  'title',
  'title-medium',
  'ui',
  'detail-strong',
  'caption-strong',
];

const WEIGHTED_WITH_LEADING = new RegExp(`^(?:${WEIGHTED.join('|')})/.+$`);

const twMerge = extendTailwindMerge<'text-role' | 'text-role-leading'>(
  {
    extend: {
      classGroups: {
        'font-size': [{ text: UNWEIGHTED }],
        'text-role': [{ text: WEIGHTED }],
      },
      conflictingClassGroups: {
        'font-size': ['text-role', 'text-role-leading'],
        'text-role': ['font-size', 'text-role-leading', 'leading', 'font-weight'],
        'text-role-leading': ['font-size', 'text-role', 'leading'],
      },
      postfixLookupClassGroups: ['text-role'],
    },
  },
  (config) => ({
    ...config,
    // Na frente: o validador de cor do `text-*` aceita qualquer valor e venceria um grupo do fim.
    classGroups: {
      'text-role-leading': [{ text: [(value: string) => WEIGHTED_WITH_LEADING.test(value)] }],
      ...config.classGroups,
    },
  }),
);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
